import type { MessageKey } from '@dijitalapp/i18n';
import type { AdminMeDto, CapabilitiesDto, ExperienceDto, NavItem, SpaceDto } from '@dijitalapp/shared';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { CallOverlay } from '../components/CallUi';
import { DeviceTrustBanner, RecoveryKeyPrompt, VerificationCenter } from '../components/E2eeUi';
import { Icon } from '../components/Icon';
import { SectionNav } from '../components/SectionNav';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { useChat } from '../lib/chat-context';
import { ADMIN_TAB_ICONS, adminTabsFor } from './AdminPage';
import { ConversationList } from './Messages';
import { SETTINGS_ICONS, SETTINGS_SECTIONS } from './SettingsPage';

interface ShellState {
  spaces: SpaceDto[];
  activeSpace: SpaceDto | null;
  experience: ExperienceDto | null;
  capabilities: CapabilitiesDto['capabilities'] | null;
  selectSpace: (id: string) => void;
  reload: () => Promise<void>;
  setMobileView: (v: 'list' | 'content') => void;
}

const ShellCtx = createContext<ShellState | null>(null);
export function useShell(): ShellState {
  const v = useContext(ShellCtx);
  if (!v) throw new Error('useShell outside Shell');
  return v;
}

interface ShellData {
  spaces: SpaceDto[];
  capabilities: CapabilitiesDto['capabilities'];
}

async function loadShellData(): Promise<ShellData> {
  const [{ spaces }, caps] = await Promise.all([
    api<{ spaces: SpaceDto[] }>('GET', '/spaces'),
    api<CapabilitiesDto>('GET', '/me/capabilities'),
  ]);
  return { spaces, capabilities: caps.capabilities };
}

const NAV_LABELS: Record<NavItem, MessageKey> = {
  messages: 'nav.messages',
  calls: 'nav.calls',
  spaces: 'nav.spaces',
  calendar: 'nav.calendar',
  learn: 'nav.learn',
  discover: 'nav.discover',
  ai: 'nav.ai',
};

export function Shell() {
  const { i18n, me, online, config } = useApp();
  const { t } = i18n;
  const chat = useChat();
  const nameOf = (userId: string) => chat.conversations.find((c) => c.peer?.id === userId)?.peer?.displayName ?? '…';
  const location = useLocation();
  const [spaces, setSpaces] = useState<SpaceDto[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [experience, setExperience] = useState<ExperienceDto | null>(null);
  // Staff link only; the API authorizes every admin call regardless of what the UI shows.
  const [adminMe, setAdminMe] = useState<AdminMeDto | null>(null);
  useEffect(() => {
    void api<AdminMeDto>('GET', '/admin/me').then(setAdminMe, () => setAdminMe(null));
  }, []);
  const isStaff = adminMe !== null;
  const [capabilities, setCapabilities] = useState<CapabilitiesDto['capabilities'] | null>(null);
  // Pathname at which the user stepped back to the list on mobile; any navigation shows content.
  const [listAt, setListAt] = useState<string | null>(null);
  // Root and the chat home show the list on phones; everything else shows its content pane.
  const section = location.pathname.startsWith('/settings') ? 'settings' : location.pathname.startsWith('/admin') ? 'admin' : null;
  const mobileView =
    ['/', '/messages', '/settings', '/admin'].includes(location.pathname) || listAt === location.pathname ? 'list' : 'content';
  const setMobileView = (v: 'list' | 'content') => setListAt(v === 'list' ? location.pathname : null);

  const apply = useCallback((data: ShellData) => {
    setSpaces(data.spaces);
    setCapabilities(data.capabilities);
    setActiveId((cur) => (cur && data.spaces.some((s) => s.id === cur) ? cur : (data.spaces[0]?.id ?? null)));
  }, []);

  const reload = useCallback(async () => apply(await loadShellData()), [apply]);

  useEffect(() => {
    let cancelled = false;
    void loadShellData().then((d) => !cancelled && apply(d));
    return () => {
      cancelled = true;
    };
  }, [apply]);

  const activeSpace = spaces.find((s) => s.id === activeId) ?? null;

  useEffect(() => {
    if (!activeSpace) return;
    void api<ExperienceDto>('GET', `/spaces/${activeSpace.id}/experience`).then(setExperience);
  }, [activeSpace]);

  const nav = experience?.persona.navigation ?? ['messages', 'calls', 'spaces', 'calendar', 'learn', 'discover', 'ai'];

  return (
    <ShellCtx.Provider value={{ spaces, activeSpace, experience, capabilities, selectSpace: setActiveId, reload, setMobileView }}>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <div className="shell" data-mobile-view={mobileView}>
        <nav className="rail" aria-label="Main">
          <NavLink to="/" className="rail-brand" aria-label={config?.brand.name ?? 'DijitalApp'} title={config?.brand.name ?? 'DijitalApp'}>
            {Array.from(config?.brand.shortName || config?.brand.name || 'D')[0]}
          </NavLink>
          <div className="rail-group">
            {nav.map((item) => (
              <NavLink key={item} to={`/${item}`} className="rail-link" title={t(NAV_LABELS[item])}>
                <span className="rail-icon">
                  <Icon name={item} size={22} />
                </span>
                <span className="rail-label">{t(NAV_LABELS[item])}</span>
              </NavLink>
            ))}
          </div>
          <div className="rail-group rail-bottom">
            {isStaff && (
              <NavLink to="/admin" className="rail-link" title={t('admin.nav')}>
                <span className="rail-icon">
                  <Icon name="shield" size={22} />
                </span>
                <span className="rail-label">{t('admin.nav')}</span>
              </NavLink>
            )}
            <NavLink to="/settings" className="rail-link" title={t('nav.settings')}>
              <span className="rail-icon">
                <Icon name="settings" size={22} />
              </span>
              <span className="rail-label">{t('nav.settings')}</span>
            </NavLink>
            {me && (
              <NavLink to="/settings/account" className="rail-avatar" title={`${me.displayName} · @${me.username}`} aria-label={t('settings.section.account')}>
                {Array.from(me.displayName.trim())[0]?.toUpperCase() ?? '?'}
              </NavLink>
            )}
          </div>
        </nav>

        <aside className="list-pane" aria-label={section ? t(section === 'settings' ? 'settings.title' : 'admin.title') : t('spaces.title')}>
          {section === 'settings' && (
            <SectionNav
              base="/settings"
              title={t('settings.title')}
              items={SETTINGS_SECTIONS.map((s) => ({
                to: `/settings/${s}`,
                label: t(`settings.section.${s}` as MessageKey),
                icon: SETTINGS_ICONS[s],
                description: t(`settings.section.${s}.desc` as MessageKey),
              }))}
              footer={<span className="muted">{config?.brand.name ?? 'DijitalApp'}</span>}
            />
          )}
          {section === 'admin' && adminMe && (
            <SectionNav
              base="/admin"
              title={t('admin.title')}
              items={adminTabsFor(adminMe.permissions).map((x) => ({ to: `/admin/${x}`, label: t(`admin.tab.${x}` as MessageKey), icon: ADMIN_TAB_ICONS[x] }))}
              footer={<span className="muted">{t('admin.roles', { roles: adminMe.roles.join(', ') })}</span>}
            />
          )}
          {!section && (
            <>
          <div className="pane-header">
            <label className="visually-hidden" htmlFor="space-switcher">
              {t('shell.activeSpace')}
            </label>
            <select
              id="space-switcher"
              className="input space-switcher"
              value={activeId ?? ''}
              onChange={(e) => setActiveId(e.target.value)}
            >
              {spaces.map((s) => (
                <option key={s.id} value={s.id}>
                  {t(`spaces.type.${s.type}` as MessageKey)} · {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="stack list-notices">
            {me && !me.emailVerified && <EmailBanner email={me.email} />}
            {me?.ageBand === 'minor' && <div className="alert alert-info">{t('shell.minorNotice')}</div>}
            {me?.accountState === 'restricted' && <div className="alert alert-info">{t('shell.guardianPending')}</div>}
          </div>
          {config?.flags.messaging ? (
            <ConversationList />
          ) : (
            <div className="empty-state">
              <Icon name="messages" size={40} />
              <p>{t('module.disabled.title')}</p>
            </div>
          )}
            </>
          )}
        </aside>

        <main className="content-pane" id="main" tabIndex={-1}>
          <DeviceTrustBanner />
          <Outlet />
        </main>
        <VerificationCenter nameOf={nameOf} />
        <RecoveryKeyPrompt />
        <CallOverlay />
      </div>
      {!online && (
        <div className="connection-banner" role="status">
          {t('conn.offline')}
        </div>
      )}
    </ShellCtx.Provider>
  );
}

function EmailBanner({ email }: { email: string }) {
  const { i18n } = useApp();
  const [status, setStatus] = useState<string | null>(null);
  const resend = async () => {
    try {
      await api('POST', '/auth/email/resend');
      setStatus(i18n.t('banner.resent'));
    } catch (err) {
      setStatus(i18n.t(errorMessageKey(err)));
    }
  };
  return (
    <div className="alert alert-info stack" style={{ gap: 'var(--space-2)' }}>
      <span>{i18n.t('banner.emailUnverified', { email })}</span>
      {status ? (
        <span role="status">{status}</span>
      ) : (
        <button className="btn" type="button" onClick={() => void resend()}>
          {i18n.t('banner.resend')}
        </button>
      )}
    </div>
  );
}

/** Shown for modules that exist in the navigation but are not yet implemented (flag off). */
export function DisabledModule({ labelKey }: { labelKey: MessageKey }) {
  const { i18n } = useApp();
  const { setMobileView } = useShell();
  return (
    <div className="content-inner">
      <button className="btn mobile-only" type="button" onClick={() => setMobileView('list')}>
        <Icon name="back" label="Back" />
      </button>
      <div className="empty-state" role="status">
        <Icon name="lock" size={40} />
        <h2>{i18n.t('module.disabled.title')}</h2>
        <p>{i18n.t('module.disabled.body', { module: i18n.t(labelKey) })}</p>
      </div>
    </div>
  );
}
